import { useEffect, useMemo, useState } from "react";
import {
	Autocomplete,
	Box,
	Button,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	LinearProgress,
	Paper,
	Stack,
	TextField,
	Tooltip,
	Typography,
	alpha,
	useTheme,
} from "@mui/material";
import {
	AccountBalanceWallet,
	Close as CloseIcon,
	EventAvailable,
} from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "../../components/MoleDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Z_INDEX } from "../../styles/zIndex";
import { createFilterOptions, Util } from "../../util/Util";
import ConfirmDialog from "../../components/ConfirmDialog";
import RefreshButton from "../../components/RefreshButton";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { useAppSocket } from "../../providers/AppSocketContext";
import { toast } from "react-toastify";
import { formatDetailValue } from "../client/situationDetailShared";
import DepositPickList, { moneyColumns, type DepositPickItem } from "./DepositPickList";
import DepositSourceReports, { sourceViewFromPickItem, type DepositSourceView } from "./DepositSourceReports";
import { clickableRef } from "./clickableRef";
import { useData } from "../../hooks/useData";
import { LocalStorageKeys } from "../../util/constants";
import { cachedListKey, localCacheDB } from "../../db/localCache";
import { businessTodayDate, parseISODay } from "../../util/businessClock";

import { addMoney, roundMoney } from "../../util/quantity";
import { canRecord } from "../../util/access";
interface Props {
	isOpen: boolean;
	toggle: () => void;
	bundle?: any;
	onChange?: () => void;
}

type DepotOption = { id: number; depot_name?: string; DepotName?: string };

type PendingItems = {
	payments: DepositPickItem[];
	refunds: DepositPickItem[];
	expenses: DepositPickItem[];
	changes: DepositPickItem[];
	wallets: DepositPickItem[];
	wallet_debits: DepositPickItem[];
};

const emptyItems = (): PendingItems => ({
	payments: [],
	refunds: [],
	expenses: [],
	changes: [],
	wallets: [],
	wallet_debits: [],
});

const itemsFromPayload = (payload: any): PendingItems => ({
	payments: payload?.payments || [],
	refunds: payload?.refunds || [],
	expenses: payload?.expenses || [],
	changes: payload?.changes || [],
	wallets: payload?.wallets || [],
	wallet_debits: payload?.wallet_debits || [],
});

const sameIsoDay = (left?: Date | null, right?: Date | null) =>
	!!left && !!right && Util.formatISODate(left) === Util.formatISODate(right);

const itemKey = (item: DepositPickItem) => `${item.kind || "row"}-${item.id}`;
const keys = (items: DepositPickItem[]) => new Set(items.map(itemKey));

const PendingModal = ({ isOpen, toggle, bundle, onChange }: Props) => {
	const theme = useTheme();
	const { appSettings } = useAppSocket();
	const { api, session, access } = useAuth();
	const { setLoadingMessage, queueReRun, t } = useUI();
	const { saveFromServer } = useData<PendingItems>(LocalStorageKeys.PENDING_DEPOSIT_ITEMS);

	const themeColor = appSettings?.UI_THEME_PRIMARY?.value || theme.palette.primary.main;
	const currency = appSettings?.BIZ_CURRENCY?.value || "USD";
	const formatMoney = (value: number) => Util.formatCurrency(value, currency);

	const rowDate = bundle?.date ? parseISODay(bundle.date) : null;
	const fromRow = Boolean(rowDate);

	const [rangeFrom, setRangeFrom] = useState<Date | null>(rowDate);
	const [rangeTo, setRangeTo] = useState<Date | null>(rowDate);
	const [depots, setDepots] = useState<DepotOption[]>([]);
	const [selectedDepots, setSelectedDepots] = useState<DepotOption[]>([]);
	const [isDepotLoading, setIsDepotLoading] = useState(false);
	const [items, setItems] = useState<PendingItems>(emptyItems());
	const [selectedPayments, setSelectedPayments] = useState<DepositPickItem[]>([]);
	const [selectedExpenses, setSelectedExpenses] = useState<DepositPickItem[]>([]);
	const [selectedChanges, setSelectedChanges] = useState<DepositPickItem[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [confirmModalOpen, setConfirmModalOpen] = useState(false);
	const [depositDate, setDepositDate] = useState<Date>(() => businessTodayDate());
	const [errorInput, setErrorInput] = useState("");
	const [sourceView, setSourceView] = useState<DepositSourceView | null>(null);

	const expensePool = useMemo(
		() => [...items.expenses, ...items.refunds, ...items.wallet_debits],
		[items.expenses, items.refunds, items.wallet_debits],
	);
	const incomePool = useMemo(
		() => [...items.payments, ...items.wallets],
		[items.payments, items.wallets],
	);

	const availablePayments = incomePool.filter((item) => !keys(selectedPayments).has(itemKey(item)));
	const availableExpenses = expensePool.filter((item) => !keys(selectedExpenses).has(itemKey(item)));
	const availableChanges = items.changes.filter((item) => !keys(selectedChanges).has(itemKey(item)));

	const incomeTotal =
		selectedPayments.reduce((sum, item) => addMoney(sum, item.amount), 0)
		+ selectedChanges.reduce((sum, item) => addMoney(sum, item.amount), 0);
	const expenseTotal = selectedExpenses.reduce((sum, item) => addMoney(sum, item.amount), 0);
	const netTotal = roundMoney(incomeTotal - expenseTotal);
	const selectedCount = selectedPayments.length + selectedExpenses.length + selectedChanges.length;

	const earliestSelected = useMemo(() => {
		const dates = [...selectedPayments, ...selectedExpenses, ...selectedChanges]
			.map((item) => (item.date ? parseISODay(item.date) : null))
			.filter((value): value is Date => Boolean(value));
		if (!dates.length) return rowDate || businessTodayDate();
		return dates.reduce((min, value) => (value < min ? value : min), dates[0]);
	}, [selectedPayments, selectedExpenses, selectedChanges, rowDate]);

	const canLoad = Boolean(rangeFrom && rangeTo && rangeFrom <= rangeTo);
	const isPermitted = canRecord(access, 'deposit');
	const isDateValid = Boolean(
		depositDate &&
		earliestSelected &&
		Util.formatISODate(depositDate) >= Util.formatISODate(earliestSelected)
	);
	const canValidate = isPermitted && selectedCount > 0 && isDateValid;

	const validateDisabledReason = useMemo(() => {
		if (isLoading) return t("common:ui.loading");
		if (!isPermitted) return t("common:deposits.modal.noPermission");
		if (selectedCount === 0) return t("common:deposits.modal.noneSelectedHint");
		if (!isDateValid) return t("common:deposits.modal.dateBeforeEarliest");
		return "";
	}, [isLoading, isPermitted, selectedCount, isDateValid, t]);

	const depotParams = selectedDepots.length
		? { Depots: selectedDepots.map((depot) => depot.id) }
		: {};

	const fetchDepots = async () => {
		setIsDepotLoading(true);
		try {
			const res = await api.get("/deposits/pending/depots");
			if (res?.status === 200 && Array.isArray(res.data)) {
				const rows = [...res.data].sort((a: DepotOption, b: DepotOption) =>
					(a.depot_name || a.DepotName || "").localeCompare(b.depot_name || b.DepotName || ""),
				);
				setDepots(rows);
			} else if (res?.status === 401 || res?.status === 422) {
				queueReRun(fetchDepots);
			}
		} catch (err) {
			console.error("fetchDepots failed", err);
		} finally {
			setIsDepotLoading(false);
		}
	};

	const fetchItems = async () => {
		if (!canLoad || !rangeFrom || !rangeTo) return;
		setIsLoading(true);
		try {
			const res = await api.get("/deposits/pending/items", {
				params: {
					From: Util.formatISODate(rangeFrom),
					To: Util.formatISODate(rangeTo),
					...depotParams,
				},
			});
			if (res?.status === 200) {
				const next = itemsFromPayload(res.data);
				setItems(next);
				setSelectedPayments([]);
				setSelectedExpenses([]);
				setSelectedChanges([]);
				void saveFromServer([next], { from: rangeFrom, to: rangeTo }, true);
			} else if (res?.status === 401 || res?.status === 422) {
				queueReRun(fetchItems);
			} else {
				setItems(emptyItems());
			}
		} catch (err) {
			console.error("fetchItems failed", err);
			setItems(emptyItems());
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		if (!isOpen) return;
		const nextDate = bundle?.date ? parseISODay(bundle.date) : null;
		setDepositDate(nextDate || businessTodayDate());
		setErrorInput("");
		setConfirmModalOpen(false);
		setSelectedPayments([]);
		setSelectedExpenses([]);
		setSelectedChanges([]);
		setSelectedDepots([]);
		void fetchDepots();

		let cancelled = false;
		(async () => {
			// Same scoped key useData writes with, otherwise this always misses.
			const cacheKey = cachedListKey(LocalStorageKeys.PENDING_DEPOSIT_ITEMS, session?.user_id);
			const cached = cacheKey ? await localCacheDB.records.get(cacheKey) : undefined;
			if (cancelled) return;
			const stored = cached?.data?.[0] as PendingItems | undefined;
			const cachedFrom = cached?.period?.from ? new Date(cached.period.from) : null;
			const cachedTo = cached?.period?.to ? new Date(cached.period.to) : null;

			if (nextDate) {
				setRangeFrom(nextDate);
				setRangeTo(nextDate);
				if (stored && sameIsoDay(cachedFrom, nextDate) && sameIsoDay(cachedTo, nextDate)) {
					setItems(stored);
					return;
				}
				setItems(emptyItems());
				setIsLoading(true);
				try {
					const res = await api.get("/deposits/pending/items", {
						params: {
							From: Util.formatISODate(nextDate),
							To: Util.formatISODate(nextDate),
						},
					});
					if (cancelled) return;
					if (res?.status === 200) {
						const next = itemsFromPayload(res.data);
						setItems(next);
						void saveFromServer([next], { from: nextDate, to: nextDate }, true);
					} else if (res?.status === 401 || res?.status === 422) {
						queueReRun(fetchItems);
					} else {
						setItems(emptyItems());
					}
				} catch (err) {
					console.error("fetchItems for nextDate failed", err);
					if (!cancelled) setItems(emptyItems());
				} finally {
					if (!cancelled) setIsLoading(false);
				}
				return;
			}

			if (stored) {
				setRangeFrom(cachedFrom);
				setRangeTo(cachedTo);
				setItems(stored);
				return;
			}
			setRangeFrom(null);
			setRangeTo(null);
			setItems(emptyItems());
		})();

		return () => {
			cancelled = true;
		};
	}, [isOpen, bundle?.date]);

	const handleDeposit = async () => {
		if (!canRecord(access, 'deposit')) return;
		if (!Util.checkDating(Util.formatISODate(depositDate), access, 'deposit')) return;
		if (selectedCount === 0) return;

		setLoadingMessage("SAVE");
		const batch = Util.getDepositNumber();
		const details = {
			batch,
			d_date: Util.formatISODate(depositDate),
		};
		const res = await api.post("deposits", {
			details,
			records: [
				...selectedPayments
					.filter((item) => item.kind !== "wallet")
					.map((item) => ({ payment: item.id, batch })),
				...selectedExpenses
					.filter((item) => item.kind === "refund")
					.map((item) => ({ payment: item.id, batch })),
			],
			expenses: selectedExpenses
				.filter((item) => item.kind === "expense")
				.map((item) => ({ expense: item.id, batch })),
			vouchers: selectedChanges.map((item) => ({ voucher: item.id, batch })),
			wallets: [
				...selectedPayments
					.filter((item) => item.kind === "wallet")
					.map((item) => ({ wallet_record: item.id, batch })),
				...selectedExpenses
					.filter((item) => item.kind === "wallet_debit")
					.map((item) => ({ wallet_record: item.id, batch })),
			],
		});

		if (res?.status === 201) {
			toast.success(t("common:toast.success.deposited"), { autoClose: 3000 });
			toggle();
			onChange?.();
		} else if (res?.status === 401 || res?.status === 422) {
			queueReRun(handleDeposit);
		}
		setLoadingMessage(null);
	};

	const panelSx = {
		p: { xs: 1.5, md: 2 },
		borderRadius: 2,
		border: "1px solid",
		borderColor: "divider",
		bgcolor: "background.paper",
	};

	const pickCommon = {
		availableLabel: t("common:deposits.modal.available"),
		selectedLabel: t("common:deposits.modal.selected"),
		emptyAvailable: t("common:deposits.modal.noneAvailable"),
		emptySelected: t("common:deposits.modal.noneSelected"),
	};

	return (
		<>
			<Dialog
				open={isOpen}
				onClose={toggle}
				maxWidth="xl"
				fullWidth
				disableEscapeKeyDown
				sx={{ zIndex: Z_INDEX.dialog }}
				PaperProps={{
					sx: {
						borderRadius: 3,
						borderTop: `3px solid ${themeColor}`,
						overflow: "hidden",
						maxHeight: "95vh",
					},
				}}
			>
				<DialogTitle
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "flex-start",
						background: `linear-gradient(180deg, ${alpha(themeColor, 0.07)} 0%, transparent 100%)`,
					}}
				>
					<Stack direction="row" spacing={1.5} alignItems="center">
						<Box
							sx={{
								width: 42,
								height: 42,
								borderRadius: 2,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								bgcolor: alpha(themeColor, 0.12),
								color: themeColor,
							}}
						>
							<AccountBalanceWallet />
						</Box>
						<Box>
							<Typography variant="h6" fontWeight={700} color={themeColor}>
								{fromRow ? t("common:deposits.modal.title") : t("common:deposits.modal.newTitle")}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								{rangeFrom && rangeTo
									? `${Util.formatDate(Util.formatISODate(rangeFrom), "medium")} – ${Util.formatDate(Util.formatISODate(rangeTo), "medium")}`
									: t("common:deposits.modal.rangeHint")}
							</Typography>
						</Box>
					</Stack>
					<IconButton onClick={toggle} aria-label={t("common:ui.buttons.cancel")}>
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent sx={{ pt: 1 }}>
					<Grid container spacing={2}>
						<Grid size={{ xs: 12, lg: 8 }}>
							<Paper elevation={0} sx={{ ...panelSx, mb: 2, bgcolor: alpha(themeColor, 0.02) }}>
								<Stack
									direction={{ xs: "column", sm: "row" }}
									spacing={1.5}
									alignItems={{ xs: "stretch", sm: "center" }}
								>
									<LocalizationProvider dateAdapter={AdapterDateFns}>
										<DatePicker
											label={t("common:dashboard.from")}
											value={rangeFrom}
											onChange={(next) => setRangeFrom(next instanceof Date || !next ? next : next.toDate())}
											slotProps={{ textField: { size: "small", fullWidth: true, variant: "filled" } }}
										/>
										<DatePicker
											label={t("common:dashboard.to")}
											value={rangeTo}
											minDate={rangeFrom || undefined}
											onChange={(next) => setRangeTo(next instanceof Date || !next ? next : next.toDate())}
											slotProps={{ textField: { size: "small", fullWidth: true, variant: "filled" } }}
										/>
									</LocalizationProvider>
									<Button
										variant="contained"
										onClick={() => void fetchItems()}
										disabled={!canLoad || isLoading}
										sx={{ minWidth: 140, height: 40 }}
									>
										{t("common:deposits.modal.load")}
									</Button>
									<RefreshButton isWorking={isLoading} onClick={() => void fetchItems()} disabled={!canLoad} />
								</Stack>
								<Autocomplete
									multiple
									id="pending-deposit-depots"
									size="small"
									options={depots}
									filterOptions={createFilterOptions((opt) => opt.depot_name || opt.DepotName || "")}
									getOptionLabel={(depot) => depot.depot_name || depot.DepotName || ""}
									value={selectedDepots}
									onChange={(_, next) => setSelectedDepots(next)}
									isOptionEqualToValue={(option, value) => option.id === value.id}
									loading={isDepotLoading}
									autoHighlight
									disableCloseOnSelect
									disabled={isDepotLoading}
									sx={{ mt: 1.5 }}
									renderInput={(params) => (
										<TextField
											{...params}
											label={
												<span>
													{t("common:deposits.modal.depots")}&nbsp;&nbsp;
													<RefreshButton isWorking={isDepotLoading} onClick={fetchDepots} disabled={isDepotLoading} />
												</span>
											}
											variant="filled"
											fullWidth
											helperText={t("common:deposits.modal.depotsHint")}
										/>
									)}
								/>
								<Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
									{t("common:deposits.modal.rangeHint")}
								</Typography>
							</Paper>

							{isLoading && <LinearProgress sx={{ mb: 1.5, borderRadius: 999 }} />}

							<Stack spacing={2}>
								<Paper elevation={0} sx={panelSx}>
									<DepositPickList
										title={t("common:deposits.modal.paymentsTitle")}
										hint={t("common:deposits.modal.paymentsHint")}
										{...pickCommon}
										available={availablePayments}
										selected={selectedPayments}
										onAdd={(next) => setSelectedPayments((prev) => [...prev, ...next])}
										onRemove={(next) => {
											const remove = new Set(next.map(itemKey));
											setSelectedPayments((prev) => prev.filter((row) => !remove.has(itemKey(row))));
										}}
										columns={moneyColumns(t, [
											{
												key: "kind",
												header: t("common:tables.type"),
												render: (item) =>
													item.kind === "wallet"
														? t("common:deposits.pendingDetail.walletCount")
														: t("common:deposits.pendingDetail.paymentCount"),
											},
											{ key: "depot", header: t("common:tables.depot"), render: (item) => item.depot || "—" },
											{ key: "client", header: t("common:tables.client"), render: (item) => item.client || "—" },
											{
												key: "ref",
												header: t("common:tables.reference"),
												render: (item) => clickableRef(
													item.reference || item.category || "—",
													() => setSourceView(sourceViewFromPickItem(item)),
												),
											},
										])}
									/>
								</Paper>
								<Paper elevation={0} sx={panelSx}>
									<DepositPickList
										title={t("common:deposits.modal.expensesTitle")}
										hint={t("common:deposits.modal.expensesHint")}
										{...pickCommon}
										available={availableExpenses}
										selected={selectedExpenses}
										onAdd={(next) => setSelectedExpenses((prev) => [...prev, ...next])}
										onRemove={(next) => {
											const remove = new Set(next.map(itemKey));
											setSelectedExpenses((prev) => prev.filter((row) => !remove.has(itemKey(row))));
										}}
										columns={moneyColumns(t, [
											{
												key: "kind",
												header: t("common:tables.type"),
												render: (item) =>
													item.kind === "refund"
														? t("common:deposits.pendingDetail.refundCount")
														: item.kind === "wallet_debit"
															? t("common:deposits.pendingDetail.walletDebitCount")
															: t("common:deposits.pendingDetail.expenseCount"),
											},
											{ key: "depot", header: t("common:tables.depot"), render: (item) => item.depot || "—" },
											{ key: "who", header: t("common:tables.client"), render: (item) => item.client || item.payee || item.category || "—" },
											{
												key: "ref",
												header: t("common:tables.reference"),
												render: (item) => clickableRef(
													item.reference || item.batch || "—",
													() => setSourceView(sourceViewFromPickItem(item)),
												),
											},
										])}
									/>
								</Paper>
								<Paper elevation={0} sx={panelSx}>
									<DepositPickList
										title={t("common:deposits.modal.changesTitle")}
										hint={t("common:deposits.modal.changesHint")}
										{...pickCommon}
										available={availableChanges}
										selected={selectedChanges}
										onAdd={(next) => setSelectedChanges((prev) => [...prev, ...next])}
										onRemove={(next) => {
											const remove = new Set(next.map(itemKey));
											setSelectedChanges((prev) => prev.filter((row) => !remove.has(itemKey(row))));
										}}
										columns={moneyColumns(t, [
											{
												key: "code",
												header: t("common:voucher.code"),
												render: (item) => clickableRef(
													item.code || "—",
													() => setSourceView(sourceViewFromPickItem(item)),
												),
											},
											{ key: "depot", header: t("common:tables.depot"), render: (item) => item.depot || "—" },
											{ key: "issuee", header: t("common:voucher.issuee"), render: (item) => item.issuee || "—" },
										])}
									/>
								</Paper>
							</Stack>
						</Grid>

						<Grid size={{ xs: 12, lg: 4 }}>
							<Stack spacing={2}>
								<Paper elevation={0} sx={{ ...panelSx, bgcolor: alpha(theme.palette.warning.main, 0.04) }}>
									<Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
										{t("common:report.summary")}
									</Typography>
									<Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
										<Typography variant="body2">{t("common:deposits.modal.income")}</Typography>
										<Typography variant="body2" fontWeight={700}>{formatDetailValue(incomeTotal)}</Typography>
									</Stack>
									<Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
										<Typography variant="body2">{t("common:deposits.modal.expense")}</Typography>
										<Typography variant="body2" fontWeight={700}>{formatDetailValue(expenseTotal)}</Typography>
									</Stack>
									<Divider sx={{ my: 1.5 }} />
									<Stack direction="row" justifyContent="space-between" alignItems="center">
										<Typography variant="subtitle1" fontWeight={700}>{t("common:deposits.modal.net")}</Typography>
										<Typography variant="h6" fontWeight={800} color="warning.main">
											{formatDetailValue(netTotal)}
										</Typography>
									</Stack>
									<Chip
										size="small"
										sx={{ mt: 1 }}
										label={t("common:deposits.pendingDetail.payments", { count: selectedCount })}
									/>
								</Paper>

								<Paper elevation={0} sx={panelSx}>
									<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
										<EventAvailable color="primary" fontSize="small" />
										<Typography variant="subtitle2" fontWeight={700}>
											{t("common:deposits.modal.depositDate")}
										</Typography>
									</Stack>
									<LocalizationProvider dateAdapter={AdapterDateFns}>
										<DatePicker
											label={t("common:tables.depositDate")}
											value={depositDate}
											minDate={earliestSelected}
											onChange={(newDate) => {
												if (!newDate) return;
												const date = newDate instanceof Date ? newDate : newDate.toDate();
												if (Util.formatISODate(date) < Util.formatISODate(earliestSelected)) {
													setErrorInput("dateInput");
													setTimeout(() => setErrorInput(""), 1500);
													return;
												}
												setErrorInput("");
												setDepositDate(date);
											}}
											slotProps={{
												textField: {
													size: "small",
													fullWidth: true,
													variant: "filled",
													error: errorInput === "dateInput",
													helperText:
														errorInput === "dateInput"
															? t("common:validation.invalidDate")
															: t("common:deposits.modal.depositDateHint"),
												},
											}}
										/>
									</LocalizationProvider>
								</Paper>
							</Stack>
						</Grid>
					</Grid>
				</DialogContent>

				<DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: "divider" }}>
					<Button variant="outlined" color="inherit" onClick={toggle}>
						{t("common:ui.buttons.cancel")}
					</Button>
					<Tooltip title={validateDisabledReason} disableHoverListener={!validateDisabledReason} arrow>
						<span>
							<Button
								variant="contained"
								color="success"
								onClick={() => setConfirmModalOpen(true)}
								disabled={!canValidate || isLoading}
							>
								{t("common:deposits.modal.validateDeposit")}
							</Button>
						</span>
					</Tooltip>
				</DialogActions>
			</Dialog>

			<DepositSourceReports view={sourceView} onClose={() => setSourceView(null)} />

			<ConfirmDialog
				onConfirm={handleDeposit}
				isOpen={confirmModalOpen}
				setIsOpen={setConfirmModalOpen}
				title={t("common:dialog.depositConfirmation")}
				message={t("common:deposits.modal.confirmMessage", {
					amount: formatMoney(netTotal),
					income: formatMoney(incomeTotal),
					expenses: formatMoney(expenseTotal),
					date: Util.formatDate(Util.formatISODate(depositDate), "full"),
				})}
				confirmBtnColor="success"
				confirmBtnText={t("common:deposits.modal.validateDeposit")}
			/>
		</>
	);
};

export default PendingModal;
